import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export const themeItems = [
  { key: 'indigo', label: 'Indigo Premium', icon: 'fa-droplet', color: '#4f46e5' },
  { key: 'emerald', label: 'Emerald Oasis', icon: 'fa-leaf', color: '#10b981' },
  { key: 'midnight', label: 'Midnight Pro', icon: 'fa-moon', color: '#0f172a' },
  { key: 'sunset', label: 'Sunset Gold', icon: 'fa-sun', color: '#f59e0b' },
  { key: 'rose', label: 'Rose Elegant', icon: 'fa-wand-magic-sparkles', color: '#e11d48' }
];

const ThemeSwitcher = ({ theme, onThemeChange, compact = false, showLabel = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);
  const menuRef = useRef(null);
  
  const currentTheme = themeItems.find(t => t.key === theme) || themeItems[0];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener('resize', handleResize);
    
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        // If it's a sheet (mobile), the backdrop handles closing.
        // If it's a dropdown (desktop), we check if click is outside.
        if (!isMobile) setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  const renderMenuContent = () => (
    <div className={isMobile ? "theme-picker-sheet" : "theme-picker-dropdown"}>
      <div className="theme-picker-header">
        <span>Select Theme</span>
        <button type="button" className="theme-picker-close" onClick={() => setIsOpen(false)}>
          <i className="fas fa-times" />
        </button>
      </div>
      <div className="theme-grid">
        {themeItems.map(item => (
          <button
            key={item.key}
            type="button"
            className={`theme-item ${theme === item.key ? 'active' : ''}`}
            onClick={() => {
              onThemeChange(item.key);
              setIsOpen(false);
            }}
          >
            <div className="theme-swatch" style={{ backgroundColor: item.color }}>
              {theme === item.key && <i className="fas fa-check" />}
            </div>
            <div className="theme-info">
              <span className="theme-name">{item.label}</span>
              <i className={`fas ${item.icon}`} style={{ color: item.color, fontSize: '0.75rem' }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="theme-switcher-container" ref={menuRef}>
      <button
        type="button"
        className={`theme-switcher-toggle ${compact ? 'compact' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Change Theme"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <i className={`fas ${currentTheme.icon}`} style={{ color: currentTheme.color }} />
        {showLabel && <span className="theme-switcher-label">Theme</span>}
        <i className={`fas fa-chevron-down toggle-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        isMobile ? (
          createPortal(
            <>
              <div className="theme-picker-backdrop" onClick={() => setIsOpen(false)} />
              {renderMenuContent()}
            </>,
            document.body
          )
        ) : (
          renderMenuContent()
        )
      )}
    </div>
  );
};

export default ThemeSwitcher;
